import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/api-clients/react-query';
import { privateApi } from '@/lib/api-clients/rest-client';
import { AuthParams, useAccountStore } from '@/store/use-account-store';
import { OrderStatus, OrderType } from '@/types/enums';

import { useUnifiedAccount } from '@/hooks';

interface GetOrdersParams {
  id?: string | string[];
  symbol?: string;
  orderType?: OrderType;
  status?: OrderStatus;
  startTime?: number;
  endTime?: number;
  limit?: number;
}

export type NewResponse = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: string;
  tpPrice?: string;
  slPrice?: string;
  size: string;
  reduceOnly: boolean;
  status: OrderStatus.NEW;
  initMarginRatio: string;
  postTime: number;
};

export type FilledResponse = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: string;
  tpPrice?: string;
  slPrice?: string;
  size: string;
  reduceOnly: boolean;
  status: OrderStatus.FILLED;
  initMarginRatio: string;
  lastFilledSize: string;
  lastFilledPrice: string;
  lastFilledTime: number;
  avgFilledPrice: string;
  realizedPnl: string;
  settledFunding: string;
  fees: string;
  postTime: number;
  markPrice: string;
};

export type CancelledResponse = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: string;
  tpPrice?: string;
  slPrice?: string;
  size: string;
  reduceOnly: boolean;
  status: OrderStatus.CANCELLED;
  initMarginRatio: string;
  postTime: number;
};

export type RejectedResponse = {
  id: string;
  symbol: string;
  isBuy: boolean;
  orderType: OrderType;
  limitPrice: string;
  tpPrice?: string;
  slPrice?: string;
  size: string;
  reduceOnly: boolean;
  status: OrderStatus.REJECTED;
  initMarginRatio: string;
  postTime: number;
  code: number;
};

export type OrderResponse =
  | NewResponse
  | FilledResponse
  | CancelledResponse
  | RejectedResponse;

export const getOrders = async ({
  id,
  symbol,
  orderType,
  status,
  startTime,
  endTime,
  limit,
}: GetOrdersParams): Promise<OrderResponse[]> => {
  return privateApi.get('/orders', {
    params: {
      id: typeof id === 'string' ? id : id?.join(','),
      symbol,
      orderType,
      status,
      startTime,
      endTime,
      limit,
    },
  });
};

export const getOrdersQueryOptions = (
  params: GetOrdersParams,
  authParams: AuthParams | undefined,
) => {
  const {
    endTime = useAccountStore.getState().getAccurateTime(),
    limit = 1000,
    ...restOfParams
  } = params;
  const paramsToCache = { ...restOfParams };

  return queryOptions({
    queryKey: ['Orders', paramsToCache, authParams?.signingKey],
    queryFn: () => getOrders(params),
  });
};

type UseOrdersOptions = GetOrdersParams & {
  queryConfig?: QueryConfig<typeof getOrdersQueryOptions>;
};

export const useOrders = ({
  queryConfig,
  ...params
}: UseOrdersOptions = {}) => {
  const { address } = useUnifiedAccount();
  const { hasAuth, authParams } = useAccountStore((state) => ({
    hasAuth: state.hasAuth,
    authParams: state.getAuthParams(),
  }));
  const result = useQuery({
    enabled: !!authParams?.signingKey && hasAuth,
    ...getOrdersQueryOptions(params, authParams),
    ...queryConfig,
  });

  return result;
};
